/**
 * Parses a date string in a flexible set of human formats and returns an ISO
 * YYYY-MM-DD string, or `null` if the input cannot be parsed into a valid
 * calendar date.
 *
 * Supported inputs (separators `/`, `-`, or `.`):
 *   - US MDY with 2- or 4-digit year: `11/1/98`, `11-01-1998`, `11.01.1998`
 *   - ISO YMD: `1998-11-01`
 *
 * Two-digit years: `00–29` expand to `2000–2029`, `30–99` to `1930–1999`
 * (covers the realistic DOB window).
 */
export function parseFlexibleDate(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  const parts = trimmed.split(/[/\-.]/)
  if (parts.length !== 3) return null
  if (parts.some(p => p.length === 0 || !/^\d+$/.test(p))) return null

  let year: number
  let month: number
  let day: number

  if (parts[0].length === 4 && parts[1].length <= 2 && parts[2].length <= 2) {
    year = Number(parts[0])
    month = Number(parts[1])
    day = Number(parts[2])
  } else if (
    parts[0].length >= 1 && parts[0].length <= 2 &&
    parts[1].length >= 1 && parts[1].length <= 2 &&
    (parts[2].length === 2 || parts[2].length === 4)
  ) {
    month = Number(parts[0])
    day = Number(parts[1])
    if (parts[2].length === 2) {
      const n = Number(parts[2])
      year = n <= 29 ? 2000 + n : 1900 + n
    } else {
      year = Number(parts[2])
    }
  } else {
    return null
  }

  if (!isValidYmd(year, month, day)) return null

  return `${pad4(year)}-${pad2(month)}-${pad2(day)}`
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (year < 1 || year > 9999) return false
  if (month < 1 || month > 12) return false
  if (day < 1) return false
  const daysInMonth = [31, isLeap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= daysInMonth[month - 1]
}

function isLeap(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function pad4(n: number): string {
  return String(n).padStart(4, '0')
}
